import { useMemo } from 'react';
import { Box } from '@mui/material';
import { AssetWrapper } from '../../assetComponents/assetWrapper/AssetWrapper';
import { BaseAssetProps } from '../../assetComponents/types';
import { useGetAssetByIdQuery } from 'src/hooks/reactQueryApiHooks/useGetAssetByIdQuery';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetMultipleLatestReadings } from 'src/hooks/reactQueryApiHooks';
import { useAssetStateData } from 'src/features/sites/hooks';
import { ComponentType } from 'src/types/SCADA/componentsTypes';
import { PumpWidgetIcon } from '../../components/pumpWidgetIcon';
import { AssetNameLabel } from '../../assetNameLabel/AssetNameLabel';
import { usePumpStateAndMode } from 'src/hooks/featureHooks/usePumpStateAndMode';
import { PumpAsset } from 'src/types/assets';

type Props = BaseAssetProps & {
  type: ComponentType;
};

export const PumpSCADA = ({ selected, width, height, data, type, id }: Props) => {
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const { asset } = useGetAssetByIdQuery({
    customerId: customerId!,
    siteId: data?.siteId,
    assetId: data?.assetId,
  });

  // Websockets
  const { assetActiveAlarm, isRunningLive } = useAssetStateData({ asset: asset! });

  const { readingsMap: multipleReadings } = useGetMultipleLatestReadings({
    items: asset?.assignedAlarmTags,
  });

  const values = useMemo(
    () =>
      Object.values(multipleReadings)?.map((tagData) => {
        const tagId = Object.keys(tagData)[0];
        const values = tagData[tagId]?.values ?? {};
        return Object.values(values)[0];
      }),
    [multipleReadings]
  );

  const isAlarmActive = useMemo(
    () =>
      assetActiveAlarm !== null
        ? assetActiveAlarm === 1
        : values.some((item) => String(item) === '1'),
    [assetActiveAlarm, values]
  );

  const { isRunning } = usePumpStateAndMode({ asset: asset as PumpAsset | undefined });

  const displayIsRunning = isRunningLive ?? isRunning;

  const finalWidth = width ?? (type === ComponentType.Rotor ? 100 : 160);
  const finalHeight = height ?? (type === ComponentType.Rotor ? 100 : 150);

  return (
    <AssetWrapper id={id} selected={selected} width={finalWidth} height={finalHeight} type={type}>
      <Box
        data-sm={type}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '8px 0',
          boxSizing: 'border-box',
        }}
      >
        <PumpWidgetIcon
          type={type}
          isRunning={displayIsRunning}
          isAlarmActive={isAlarmActive}
          width={finalWidth}
          height={finalHeight * 0.7}
        />

        {data?.isDisplayName && (
          <Box sx={{ textAlign: 'center' }}>
            <AssetNameLabel assetName={asset?.assetName} />
          </Box>
        )}
      </Box>
    </AssetWrapper>
  );
};
